import { NavLink } from 'react-router-dom';
import { Menu, Settings, X, Home, Calendar, CheckSquare, List } from 'lucide-react';
import { useSidebarState } from '../hooks/useSidebarState';
import { useIsDesktop, useIsPhone, useIsTablet, useIsNarrowDesktop } from '../hooks/useMediaQuery';
import { MiniCalendar } from './sidebar/MiniCalendar';
import { SidebarNavIcons } from './sidebar/SidebarNavIcons';
import { SidebarChoreList } from './sidebar/SidebarChoreList';
import { FamilyMemberLegend } from './sidebar/FamilyMemberLegend';
import { TimeTile } from './sidebar/TimeTile';
import { ReconnectWarning } from './sidebar/ReconnectWarning';
import { analytics } from '../lib/analytics';
import { useSelectedDate } from '../context/DateContext';
import { useEventsRange } from '../api/hooks';

const collapsedNavItems = [
  { to: '/', icon: Home, label: 'Home' },
  { to: '/calendar', icon: Calendar, label: 'Calendar' },
  { to: '/chores', icon: CheckSquare, label: 'Chores' },
  { to: '/lists', icon: List, label: 'Lists' },
];

/**
 * Main sidebar component with responsive behavior
 *
 * Desktop (>= 768px): Full 280px sidebar with all features
 * Mobile (< 768px): Collapsed 72px with icons only, hamburger to expand
 *
 * Layout:
 * - Header with logo (and hamburger on mobile)
 * - Mini month calendar
 * - Horizontal navigation icons
 * - Today's chores list
 * - Settings button pinned to bottom
 */
export function Sidebar() {
  const isDesktop = useIsDesktop();
  const isPhone = useIsPhone();
  const isTablet = useIsTablet();
  const isNarrowDesktop = useIsNarrowDesktop();
  const { isExpanded, toggleSidebar, collapseSidebar } = useSidebarState();
  const { selectedDate } = useSelectedDate();
  const { data: eventsData } = useEventsRange(365);

  const members = eventsData?.members || [];
  const eventsByDate = eventsData?.eventsByDate || {};

  // Hide sidebar on phones and tablets (use BottomTabBar instead)
  if (isPhone || isTablet) {
    return null;
  }

  const handleToggle = () => {
    toggleSidebar();
    analytics.sidebarToggled(!isExpanded);
  };

  // Collapsed state (mobile only)
  if (!isExpanded) {
    return (
      <aside className="sidebar sidebar--collapsed lg:w-20">
        {/* Logo and hamburger */}
        <div className="flex flex-col items-center py-4 border-b border-border-light">
          <button
            onClick={handleToggle}
            className="p-2.5 rounded-xl hover:bg-bg-secondary transition-all duration-200 mb-3"
            aria-label="Open sidebar"
          >
            <Menu className="w-6 h-6 text-text-secondary" />
          </button>
          <NavLink to="/calendar" aria-label="Go to calendar">
            <img
              src="/my-family-calendar-primary-icon.png?v=2"
              alt="My Family Calendar"
              className="w-11 h-11 rounded-xl shadow-sm hover:shadow-md transition-shadow"
            />
          </NavLink>
        </div>

        {/* Collapsed nav icons (vertical) */}
        <nav className="flex-1 flex flex-col py-3 px-2 gap-1">
          {collapsedNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `nav-item rounded-xl ${isActive ? 'active' : ''}`
              }
            >
              <item.icon className="w-6 h-6" />
            </NavLink>
          ))}
        </nav>

        {/* Settings at bottom (with reconnect warning above it) */}
        <div className="sidebar-settings py-3 px-2">
          <ReconnectWarning collapsed />
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-3 rounded-xl text-text-secondary hover:bg-bg-secondary transition-all duration-200 ${isActive ? 'text-teal bg-teal/10' : ''}`
            }
          >
            <Settings className="w-6 h-6" />
          </NavLink>
        </div>
      </aside>
    );
  }

  // Expanded state (desktop always, mobile when toggled)
  // On narrow desktop (768-1023px, e.g. iPad mini), use a narrower sidebar
  // so the main content area has enough room. lg:w-80 = 320px kicks in at 1024px+.
  return (
    <aside className={`sidebar lg:w-80 ${isNarrowDesktop ? 'sidebar--narrow' : ''}`}>
      {/* Header */}
      <div className="sidebar-header">
        <NavLink to="/calendar" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <img
            src="/my-family-calendar-primary-icon.png?v=2"
            alt="My Family Calendar"
            className="w-11 h-11 rounded-xl shadow-sm"
          />
          <span className={`font-display font-semibold text-text-primary ${isNarrowDesktop ? 'hidden' : ''}`}>My Family Calendar</span>
        </NavLink>

        {/* Mobile close button */}
        {!isDesktop && (
          <button
            onClick={collapseSidebar}
            className="p-2.5 rounded-xl hover:bg-bg-secondary transition-all duration-200"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        )}
      </div>

      {/* Time Tile */}
      <div className="px-4 py-3 lg:px-6 lg:py-4">
        <TimeTile />
      </div>

      {/* Mini Calendar */}
      <MiniCalendar
        selectedDate={selectedDate}
        eventsByDate={eventsByDate}
        members={members}
      />

      {/* Navigation Icons */}
      <SidebarNavIcons showLabels={true} />

      {/* Today's Chores */}
      <SidebarChoreList />

      {/* Family Member Color Legend */}
      <FamilyMemberLegend />

      {/* Settings at bottom (with reconnect warning above it) */}
      <div className="sidebar-settings">
        <ReconnectWarning />
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-xl mx-2 text-text-secondary hover:bg-bg-secondary transition-all duration-200 ${isActive ? 'text-teal bg-teal/10' : ''}`
          }
        >
          <Settings className="w-5 h-5" />
          <span className="text-sm font-medium">Settings</span>
        </NavLink>
      </div>
    </aside>
  );
}
